// @ts-nocheck
import React, { FC, useRef, useState, useEffect } from 'react';
import {
  showBillRedirectState,
  userState,
  viewState,
  offerState
} from '../../atoms';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  cardOutline,
  checkmark,
  cloudUploadOutline,
  hourglassOutline,
  walletOutline
} from 'ionicons/icons';
import { viewBills, viewProviders, viewOffers } from '../../constants/global';
import { Notification, PaymentInfo, AlertModal, CreditCardForm } from '../../components'
import { signupInProgress } from '../../utils/signupInProgress';
import {
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonModal,
  IonRow,
} from '@ionic/react';
import { PageProps } from "../Page.model";
import SignUpFlow from "./SignUpFlow";
import { NotificationVariant } from "../../components/Notification/Notification.model";
import './Dashboard.css'

const Dashboard: FC<PageProps> = ({ show }) => {
  const user = useRecoilValue(userState);
  const offers = useRecoilValue(offerState);
  const setUser = useSetRecoilState(userState);
  const setView = useSetRecoilState(viewState);
  const setShowBillRedirect = useSetRecoilState(showBillRedirectState);

  const modalCreditCard = useRef<HTMLIonModalElement>(null);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [hasNewOffers, setHasNewOffers] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');

  const getNotifications = () => {
    let notifications = [];
    if(user) {
      if(!user?.hasPaymentMethod && user?.paymentOption !== 'MANUAL') {
        notifications.push(
          <Notification icon={cardOutline} title="Add payment details" key="payment" subTitle="Please add your credit card details so we can process your payments">
            <IonButton className="item-dashboard-button" onClick={() => modalCreditCard.current?.present()}>Add credit card</IonButton>
          </Notification>
        );
      }
      if(['CONTACTING_PROVIDERS', 'MANUAL_INVOICE_SEND'].includes(user.status) && !user.hasBills) {
        notifications.push(
          <Notification
            icon={hourglassOutline}
            title="Waiting for bills to arrive"
            key="bill_wait"
            variant={NotificationVariant.DEFAULT}
            subTitle={
              <>
                We have not received any bills yet. <br/>
                If you want, we can redirect your bills for you.
                Just <a href="#" className="underline" onClick={event => {setView(viewProviders);event.preventDefault();}}>select your providers</a> and
                sign the authorisation.
              </>
            }
          />
        );
      }
      if(user.hasUnapprovedBills) {
        notifications.push(
          <Notification
            icon={checkmark}
            title="Bill received"
            key="bill_approval"
            variant={NotificationVariant.WARNING}
            subTitle={
              <>
                We have received a new bill that needs to be approved.
              </>
            }
          >
            <IonButton
              className="item-dashboard-button"
              onClick={() => {
                setUser({ ...user, billsReceived: true })
                setView(viewBills)
              }}
            >
              View Bills
            </IonButton>
          </Notification>
        );
      }
      if(!['CONTACTING_PROVIDERS', 'MANUAL_INVOICE_SEND'].includes(user?.status)
        && (!user?.providers?.length || user?.providers?.find(provider => provider?.status === 'NEW'))) {
        notifications.push(
          <Notification
            icon={cloudUploadOutline}
            title="Redirect your bills"
            key="authorisation"
            subTitle={
              <>
                So we can manage your bills, you need to redirect your bills to Billsmoov for the following providers:<br/>
                <ul>
                  {user?.providers?.filter(provider => provider?.status === 'NEW').map(provider => <li key={provider.id}>{provider?.name} <small>{provider?.service}</small></li>)}
                </ul>
              </>
            }
          >
            <IonButton className="item-dashboard-button" onClick={() => setShowBillRedirect(true)}>Redirect bills</IonButton>
          </Notification>
        );
      }
      if(user?.providers?.length && user?.providers?.find(provider => provider.status === 'SIGNED' || provider?.status === 'REQUESTED')) {
        notifications.push(
          <Notification
            icon={hourglassOutline}
            title="Bill redirect requested"
            key="redirectRequested"
            variant={NotificationVariant.DEFAULT}
            subTitle={
              <>
                We will notify you once your bill redirect requests have been approved.<br/>
                Redirect requests currently in progress:
                <ul>
                  {user?.providers?.filter(provider => provider?.status === 'SIGNED').map(provider => <li key={provider?.id}>{provider?.name} <small>{provider?.service}</small></li>)}
                </ul>
              </>
            }
          >
            <IonButton className="item-dashboard-button" onClick={() => setView(viewProviders)}>View providers</IonButton>
          </Notification>
        );
      }
      if(hasNewOffers) {
        notifications.push(
          <Notification icon={walletOutline} title="New offers" key="offers" subTitle={"We have found better " + offers.map(offer => offer.service).join("& ") + " deals for you."}>
            <IonButton className="item-dashboard-button" onClick={() => setView(viewOffers)}>View Offers</IonButton>
          </Notification>
        );
      }
      if(!notifications.length) {
        notifications.push(
          <Notification
            icon={checkmark}
            title="No notifications"
            key="setup_complete"
            variant={NotificationVariant.DEFAULT}
            subTitle="Your account is setup and there are no actions to complete."
          >
            <IonButton className="item-dashboard-button" onClick={() => setView(viewBills)}>View bills</IonButton>
          </Notification>
        );
      }
    }
    return (
      <IonGrid>
        <IonRow className="dashboard-row">
          {notifications.map((item, id) =>
            <IonCol key={id} className="dashboard-row-col" size="6">
              {item}
            </IonCol>
          )}
        </IonRow>
      </IonGrid>
    )
  }

  useEffect(() => {
    if(offers) {
      setHasNewOffers(!!offers.length
        && offers.some(offer => offer.status === 'NEW')
        && (offers.some(offer => (new Date() - new Date(offer.date)) < 30 * 24 * 60 * 60 * 1000)
          || offers.some(offer => new Date(user.lastLogin) < new Date(offer.date) )));
    }}, [offers]);
  
  return ( show &&
    <IonContent>
      { signupInProgress(user) ? <SignUpFlow /> :
        <>
          <PaymentInfo  />
          { getNotifications() }
        </> }
      <AlertModal
        isOpen={isOpenModal}
        title={modalMessage}
        onClosed={() => setIsOpenModal(false)}
        />
      <IonModal ref={modalCreditCard} trigger="creditCard" className="credit-card-modal">
        <div className="credit-card-modal-content" >
          <CreditCardForm onClose={() => modalCreditCard.current?.dismiss()} />
        </div>
      </IonModal>
    </IonContent>
);}

export default Dashboard;
